<template>
    <section class="kiosk-offer-overview">
        <ScrollableView class="item-categories" v-if="!offer.isLoading && offer.hasItemCategories">
            <OfferItemCategoriesList
                :useCustomColor="true"
                :showOfferCategoryImages="true"
                @categorySelected="onItemsCategorySelected"
            />
        </ScrollableView>
        <div class="offer-items-column" v-if="!offer.isLoading && offer.hasItemCategories">
            <ArticleAttributeFilter
                ref="attributeFilter"
                :items="filterItems"
                @selectionChanged="onAttributeSelectionChanged"
            />

            <ScrollableView ref="itemsContainer" class="offer-items-section">
                <div class="no-filter-result" v-if="hasNoFilterResult">
                    <button type="button" class="remove-filters" @click="onRemoveAllFiltersClick">
                        {{ translations.offerSection.removeAllFilters }}
                    </button>
                </div>
                <template v-else-if="isHomeScreen">
                    <section class="highlight-categories" v-if="filteredHighlightCategories.length > 0">
                        <div
                            v-for="category in filteredHighlightCategories"
                            :key="category.id"
                            class="highlight-tile"
                            :class="{
                                normal: category.highlightType === OfferCategoryHighlightType.Normal,
                                'full-width': category.highlightType === OfferCategoryHighlightType.FullWidth,
                            }"
                            @click="openCategory(category)"
                        >
                            <div
                                v-if="category.highlightType === OfferCategoryHighlightType.FullWidth"
                                style="position: relative"
                            >
                                <img class="banner" :src="category.imagePath" alt="" />
                                <div class="blackdim" />
                                <div class="name">
                                    <h2>{{ Format.htmlEscape(Format.translate(category.name, clientLanguage())) }}</h2>
                                    <div class="description" v-if="category.description != null">
                                        {{
                                            Format.htmlEscape(Format.translate(category.description, clientLanguage()))
                                        }}
                                    </div>
                                </div>
                            </div>

                            <template v-else>
                                <div class="name">{{ translateByLocationLanguage(category.name) }}</div>
                                <div class="image">
                                    <img :src="category.squareImagePath ?? category.imagePath" alt="" />
                                </div>
                            </template>
                        </div>
                    </section>
                    <h2 class="items-title" v-if="homeScreenDescription">{{ homeScreenDescription }}</h2>
                    <section class="items-container">
                        <OfferItemTile
                            v-for="item in homeItems"
                            :key="item.articleId"
                            :item="item"
                            :isAvailable="true"
                            @addToCartPopoverClick="selectItem(item)"
                            @quickAddClick="quickAddItem(item)"
                        />
                    </section>
                </template>
                <section class="items-container" v-else>
                    <OfferItemTile
                        v-for="item in filteredItems"
                        :key="item.articleId"
                        :item="item"
                        :isAvailable="true"
                        @addToCartPopoverClick="selectItem(item)"
                        @quickAddClick="quickAddItem(item)"
                    />
                </section>
            </ScrollableView>
        </div>
        <div class="no-active-offer-msg" v-if="!offer.isLoading && !offer.hasItemCategories">
            <span>{{ translations.offerSection.noActiveOffer }}</span>
        </div>
        <div class="offer-loader-container" v-if="offer.isLoading">
            <span>{{ translations.offerSection.loadingData }}</span>
        </div>
    </section>
</template>

<script setup lang="ts">
    import type { ICustomerArticleWithOptions, ICustomerCategory } from "@/data/model/Customer";
    import { OfferCategoryHighlightType } from "@/data/model/Enums";
    import { useOfferStore } from "@/data/stores/offer";
    import { Format } from "@/data/util/Format";
    import { clientLanguage, translateByLocationLanguage } from "@/shared/localization/language";
    import { translations } from "@/shared/localization/translations";
    import { computed, ref } from "vue";

    import ArticleAttributeFilter from "@/app/components/order/ArticleAttributeFilter.vue";
    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import OfferItemCategoriesList from "@/components/offer/OfferItemCategoriesList.vue";
    import OfferItemTile from "@/components/offer/OfferItemTile.vue";
    import { useArticleAttributesStore } from "@/data/stores/articleAttribute";

    const offer = useOfferStore();
    const articleAttributes = useArticleAttributesStore();

    const emit = defineEmits<{
        addToCartPopoverClick: [article: ICustomerArticleWithOptions];
        quickAddClick: [article: ICustomerArticleWithOptions];
    }>();

    // the first offer category acts as the "Highlights" home screen
    const isHomeScreen = computed(
        () => offer.activeCategory != null && offer.activeCategory === offer.itemCategories[0]
    );

    // description of the home screen category, shown as the title above the items
    const homeScreenDescription = computed(() => {
        const description = offer.itemCategories[0]?.description;

        return description != null ? translateByLocationLanguage(description) : null;
    });

    const activeIncludeIds = ref<Array<string>>([]);
    const activeExcludeIds = ref<Array<string>>([]);

    // the home screen filters its own items and the highlight tiles alike, so the bar has to offer what
    // either of them carries. every highlight counts, not just the ones left after filtering, otherwise
    // picking an attribute would take the other ones off the bar
    const filterItems = computed(() =>
        isHomeScreen.value
            ? [...offer.offerItems, ...offer.highlightCategories.flatMap((category) => category.articles)]
            : offer.offerItems
    );

    // an item has to carry every active include attribute and none of the active exclude ones
    const filteredItems = computed(() => {
        if (activeIncludeIds.value.length === 0 && activeExcludeIds.value.length === 0) return offer.offerItems;

        return offer.offerItems.filter((item) => {
            const attributeIds = articleAttributes.filterAttributesForArticle(item.articleId).map(c => c.id);
            if (!activeIncludeIds.value.every((id) => attributeIds.includes(id))) return false;
            if (activeExcludeIds.value.some((id) => attributeIds.includes(id))) return false;

            return true;
        });
    });

    const filteredHighlightCategories = computed(() => {
        if (activeIncludeIds.value.length === 0 && activeExcludeIds.value.length === 0)
            return offer.highlightCategories;

        return offer.highlightCategories.filter((item) => {
            const attributeIds = item.articles.flatMap((item) => articleAttributes.filterAttributesForArticle(item.articleId).map(c => c.id));
            if (!activeIncludeIds.value.every((id) => attributeIds.includes(id))) return false;
            if (activeExcludeIds.value.some((id) => attributeIds.includes(id))) return false;

            return true;
        });
    });

    const homeItems = computed(() => filteredItems.value);

    // the active filter left nothing to show - on the home screen the highlight tiles count too, so the
    // button only takes over once both of them are empty
    const hasNoFilterResult = computed(() => {
        if (activeIncludeIds.value.length === 0 && activeExcludeIds.value.length === 0) return false;

        return isHomeScreen.value
            ? filteredItems.value.length === 0 && filteredHighlightCategories.value.length === 0
            : filteredItems.value.length === 0;
    });

    function onRemoveAllFiltersClick() {
        attributeFilter.value?.clearSelection();
    }

    function onAttributeSelectionChanged(selection: { includeIds: Array<string>; excludeIds: Array<string> }) {
        activeIncludeIds.value = selection.includeIds;
        activeExcludeIds.value = selection.excludeIds;
        itemsContainer.value?.scrollToTop();
    }

    function selectItem(item: ICustomerArticleWithOptions) {
        emit("addToCartPopoverClick", item);
    }

    function quickAddItem(item: ICustomerArticleWithOptions) {
        emit("quickAddClick", item);
    }

    function openCategory(category: ICustomerCategory) {
        // the guest drilled into a highlight from the home screen, so the filter set there follows them.
        // a tile for the category that is already open changes nothing, and raising the flag for it would
        // leave it standing for the next switch, which is the sidebar's and drops what is not offered
        if (category !== offer.activeCategory) {
            attributeFilter.value?.keepSelection();
        }

        offer.setActiveCategory(category);
        itemsContainer.value?.scrollToTop();
    }

    const attributeFilter = ref<InstanceType<typeof ArticleAttributeFilter> | null>(null);

    const itemsContainer = ref<InstanceType<typeof ScrollableView> | null>(null);
    // picking a category from the sidebar starts over, highlighted or not - only drilling into a
    // highlight tile from the home screen carries the filter along
    function onItemsCategorySelected() {
        attributeFilter.value?.clearSelection();
        itemsContainer.value?.scrollToTop();
    }
</script>

<style scoped lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    .kiosk-offer-overview {
        font-size: 14px * variables.$kioskScale;
        position: absolute;
        // below the enlarged kiosk head: the scaled 52px header plus its unscaled 1px border
        top: 52px * variables.$kioskScale + 1px;
        right: 0;
        bottom: 0;
        left: 0;
        box-sizing: border-box;
        display: flex;

        * {
            box-sizing: border-box;
        }

        .item-categories {
            flex: 0 0 auto;
            background-color: colors.$lightGreyBackground;
            border-right: 1px solid colors.$greyBorder;
            position: relative;

            min-width: 85px * variables.$kioskScale;
            width: 28%;
            // the app column, scaled: the row carries the category image and the name at
            // the kiosk scale, so the column has to follow them
            max-width: 230px * variables.$kioskScale;
        }

        // holds the fixed attribute filter above the scrollable items
        .offer-items-column {
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;
            min-width: 0;
            position: relative;

            > .article-attribute-filter {
                flex: 0 0 auto;
            }
        }

        .offer-items-section {
            flex: 1 1 auto;
            min-height: 0;

            // fills the visible area of the scrollable content, so the button sits in its middle
            .no-filter-result {
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 3%;

                > .remove-filters {
                    height: 46px * variables.$kioskScale;
                    padding: 0 24px * variables.$kioskScale;
                    border: none;
                    border-radius: 4px * variables.$kioskScale;
                    background-color: colors.$orange;
                    color: #fff;
                    font-size: 17px * variables.$kioskScale;
                    letter-spacing: 0.5px;
                    cursor: pointer;

                    &:active {
                        background-color: colors.$orangeHover;
                    }
                }
            }

            .highlight-categories {
                display: flex;
                flex-wrap: wrap;
                align-content: flex-start;
                justify-content: space-between;
                padding: 3% 3% 0 3%;

                .highlight-tile {
                    margin-bottom: 3%;
                    cursor: pointer;
                    border-radius: 3px * variables.$kioskScale;
                    overflow: hidden;

                    &.normal {
                        flex: 0 0 48%;
                        max-width: 48%;
                        min-height: 60px * variables.$kioskScale;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 11px * variables.$kioskScale;
                        background: #fff;
                        border: 1px solid #dcdcdc;

                        > .name {
                            flex: 1 1 auto;
                            padding-right: 5px * variables.$kioskScale;
                            font-size: 25px * variables.$kioskScale;
                            font-weight: 700;
                            color: colors.$fontColor;
                            word-break: break-word;
                        }

                        > .image {
                            flex: 0 0 auto;
                            width: 53px * variables.$kioskScale;
                            height: 53px * variables.$kioskScale;

                            > img {
                                width: 100%;
                                height: 100%;
                                object-fit: contain;
                            }
                        }
                    }

                    &.full-width {
                        flex: 0 0 100%;
                        max-width: 100%;

                        .banner {
                            display: block;
                            width: 100%;
                            aspect-ratio: 4 / 1;
                            object-fit: cover;
                        }

                        .blackdim {
                            position: absolute;
                            left: 0;
                            top: 0;
                            right: 0;
                            bottom: 0;
                            z-index: 22;
                            background-color: #00000080;
                        }

                        .name {
                            position: absolute;
                            top: 0;
                            right: 0;
                            bottom: 0;
                            left: 0;
                            z-index: 25;
                            display: flex;
                            flex-direction: column;
                            color: #fff;
                            text-align: center;
                            justify-content: center;
                            align-items: center;

                            > h2 {
                                font-size: 25px * variables.$kioskScale;
                                margin: 0;
                            }

                            > .description {
                                font-size: 13px * variables.$kioskScale;
                                font-weight: 300;
                                margin: 0 1em;
                            }
                        }
                    }
                }
            }

            .items-title {
                margin: 0;
                padding: 0 3%;
                text-align: left;
                font-size: 25px * variables.$kioskScale;
                font-weight: 700;
                color: colors.$fontColor;
            }

            .items-container {
                flex: 1 1 auto;
                display: flex;
                flex-wrap: wrap;
                align-content: flex-start;
                justify-content: space-between;
                padding: 3%;

                .offer-item-tile {
                    flex: 0 0 48%;
                    margin-bottom: 3%;
                    max-width: 48%;
                    min-width: 85px * variables.$kioskScale;

                    font-size: 15px * variables.$kioskScale;
                }
            }

            &.is-preorder-offer {
                margin-top: -36px * variables.$kioskScale;
            }
        }

        .no-active-offer-msg,
        .offer-loader-container {
            flex: 1 1 auto;
            display: flex;
            justify-content: center;
            align-items: center;
            background: #f5f5f5;

            span {
                color: colors.$fontColorHover;
                font-size: 20px * variables.$kioskScale;
            }
        }
    }
</style>
